import { useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// Flatten a (possibly nested) color token object into a flat list of { name, value },
// where `name` is a dotted path and `value` is a color string. Non-string leaves are
// skipped. Handles both flat ({ teal500: '#...' }) and nested ({ primary: { main: '#...' }}).
function flattenColors(obj, prefix = '') {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).flatMap(([key, value]) => {
    const name = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') return [{ name, value }];
    if (value && typeof value === 'object') return flattenColors(value, name);
    return [];
  });
}

// Ionicons-style gallery for design-token colors: a searchable grid of swatches with
// click-to-copy, plus a light/dark switch that swaps between the Colors and DarkColors
// token sets so you can see how each token changes. `light`/`dark` are the resolved token
// objects (producer feedback F7); when absent the gallery shows a pending note.
function ColorGallery({ light, dark, t }) {
  const [darkMode, setDarkMode] = useState(false);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState('');

  const active = darkMode ? dark : light;
  const allColors = useMemo(() => flattenColors(active), [active]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allColors;
    return allColors.filter(
      color =>
        color.name.toLowerCase().includes(query) ||
        color.value.toLowerCase().includes(query)
    );
  }, [allColors, search]);

  const handleCopy = color => {
    if (navigator.clipboard) navigator.clipboard.writeText(color.value);
    setCopied(color.name);
  };

  const hasColors = flattenColors(light).length > 0 || flattenColors(dark).length > 0;
  if (!hasColors) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('internalLibraries.colors_pending', 'Color values not available yet (pending producer feedback F7).')}
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t('internalLibraries.colors_search_placeholder', 'Search colors…')}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        <FormControlLabel
          control={<Switch checked={darkMode} onChange={event => setDarkMode(event.target.checked)} />}
          label={darkMode ? t('internalLibraries.colors_dark', 'Dark') : t('internalLibraries.colors_light', 'Light')}
          disabled={!dark}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('internalLibraries.colors_count', { defaultValue: '{{count}} colors', count: filtered.length })}
      </Typography>

      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          bgcolor: darkMode ? '#0f172a' : '#ffffff',
          border: '1px solid',
          borderColor: 'divider',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 1,
        }}>
        {filtered.map(color => (
          <Tooltip key={color.name} title={`${color.value} — click to copy`}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => handleCopy(color)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') handleCopy(color);
              }}
              sx={{ cursor: 'pointer' }}>
              <Box
                sx={{
                  height: 56,
                  borderRadius: 1.5,
                  background: color.value,
                  border: '1px solid rgba(0,0,0,0.12)',
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  mt: 0.5,
                  color: darkMode ? '#e2e8f0' : 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {color.name}
              </Typography>
              <Typography sx={{ fontSize: 10, color: darkMode ? '#94a3b8' : 'text.secondary' }}>
                {color.value}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      <Snackbar
        open={Boolean(copied)}
        autoHideDuration={1500}
        onClose={() => setCopied('')}
        message={t('internalLibraries.colors_copied', { defaultValue: 'Copied {{name}}', name: copied })}
      />
    </Box>
  );
}

export default ColorGallery;
